/**
 * Azure Blob Storage Image Upload Service
 * Handles direct upload of images to Azure Blob Storage with SAS token
 *
 * Uses environment variables:
 * - VITE_AZURE_CONTAINER_URL: Azure container URL
 * - VITE_AZURE_SAS_TOKEN: SAS token for upload permissions
 */

import { createLogger } from '../config/environment';

const logger = createLogger('AzureBlobService');

interface UploadImageResponse {
  url: string;
  fileName: string;
  size: number;
}

class AzureBlobStorageService {
  private containerUrl: string | null = null;
  private sasToken: string | null = null;
  private initialized: boolean = false;

  /**
   * Initialize Azure Storage configuration from environment
   * This is called lazily on first use, not at module load
   */
  private initializeFromEnvironment() {
    if (this.initialized) {
      return;
    }

    console.log('🔵 [Azure] Initializing Azure Storage from environment variables');

    // Read directly from import.meta.env
    this.containerUrl = import.meta.env.VITE_AZURE_CONTAINER_URL;
    this.sasToken = import.meta.env.VITE_AZURE_SAS_TOKEN;

    // Clean and validate URLs
    if (this.containerUrl) {
      this.containerUrl = this.containerUrl.trim().replace(/\/+$/, '');
      console.log('🔧 [Azure] Cleaned container URL:', this.containerUrl);
    }

    if (this.sasToken) {
      this.sasToken = this.sasToken.trim().replace(/[\r\n]/g, '');
      console.log('🔧 [Azure] Cleaned SAS token length:', this.sasToken.length);
    }

    console.log('📋 [Azure] Environment variables:', {
      containerUrl: this.containerUrl ? `✓ present (${this.containerUrl.substring(0, 50)}...)` : '✗ missing',
      sasToken: this.sasToken ? `✓ present (${this.sasToken.length} chars)` : '✗ missing',
    });

    if (!this.containerUrl) {
      console.error('❌ [Azure] VITE_AZURE_CONTAINER_URL not found in environment variables');
      console.log('📍 [Azure] Available env keys with AZURE:', Object.keys(import.meta.env).filter(k => k.includes('AZURE')));
    }

    if (!this.sasToken) {
      console.error('❌ [Azure] VITE_AZURE_SAS_TOKEN not found in environment variables');
    }

    if (this.containerUrl && this.sasToken) {
      console.log('🟢 [Azure] Azure Storage initialized successfully');
    }

    this.initialized = true;
  }

  /**
   * Upload image file to Azure Blob Storage
   * @param file - Image file from device
   * @param fileName - Optional custom filename
   * @returns Upload response with image URL
   */
  async uploadImage(file: Blob | File, fileName?: string): Promise<UploadImageResponse> {
    // Lazy initialization on first use
    this.initializeFromEnvironment();

    console.log('🔵 [Azure] Starting image upload to Azure Blob Storage');
    console.log('📋 [Azure] File details:', {
      name: (file as File).name || fileName || 'unknown',
      size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      type: file.type,
    });

    if (!this.containerUrl || !this.sasToken) {
      const errorMsg = 'Azure Storage not configured. Please ensure VITE_AZURE_CONTAINER_URL and VITE_AZURE_SAS_TOKEN are set in .env file';
      console.error('❌ [Azure]', errorMsg);
      throw new Error(`Image upload failed: ${errorMsg}`);
    }

    try {
      // Validate file
      const validation = this.validateImageFile(file as File);
      if (!validation.valid) {
        const errorMsg = validation.error || 'Invalid image file';
        console.warn('⚠️ [Azure]', errorMsg);
        throw new Error(`Validation failed: ${errorMsg}`);
      }

      // Generate unique blob name
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 8);
      const fileExtension = (file as File).name?.split('.').pop() || 'jpg';
      const blobName = `shop-image-${timestamp}-${randomString}.${fileExtension}`;

      console.log('📝 [Azure] Generated blob name:', blobName);

      // Construct blob URL with SAS token
      const blobUrl = `${this.containerUrl}/${blobName}?${this.sasToken}`;

      console.log('🔗 [Azure] Blob URL constructed (first 100 chars):', blobUrl.substring(0, 100));

      console.log('⏳ [Azure] Uploading file to Azure Blob Storage...');

      // Upload to Azure with metadata headers
      const response = await fetch(blobUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type || 'image/jpeg',
          'x-ms-meta-uploadedby': 'myshop-admin-portal',
          'x-ms-meta-timestamp': new Date().toISOString(),
          'x-ms-meta-filename': (file as File).name || fileName || 'image',
        },
        body: file,
      });

      console.log('📡 [Azure] Azure response received:', {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
      });

      if (!response.ok) {
        console.error('❌ [Azure] Upload failed with status:', response.status);
        const errorText = await response.text();
        console.error('❌ [Azure] Error details:', errorText);
        throw new Error(`Azure upload failed: ${response.statusText}`);
      }

      // Return public URL (without SAS token for storage in database)
      const publicUrl = `${this.containerUrl}/${blobName}`;
      console.log('✅ [Azure] Image uploaded successfully');
      console.log('🔗 [Azure] Public URL:', publicUrl);

      return {
        url: publicUrl,
        fileName: blobName,
        size: file.size,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ [Azure] Error uploading image to Azure:', errorMessage);
      throw new Error(`Image upload failed: ${errorMessage}`);
    }
  }

  /**
   * Validate image file
   * @param file - File to validate
   * @returns Validation result with error message if invalid
   */
  private validateImageFile(file: File): { valid: boolean; error?: string } {
    console.log('🔵 [Validation] Validating image file:', file.name);

    const maxFileSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

    if (!allowedTypes.includes(file.type)) {
      const error = `Only JPEG, PNG, WebP, and GIF images are allowed. Got: ${file.type}`;
      console.warn('⚠️ [Validation]', error);
      return { valid: false, error };
    }

    if (file.size > maxFileSize) {
      const error = `Image size must be less than 5MB. Got: ${(file.size / 1024 / 1024).toFixed(2)} MB`;
      console.warn('⚠️ [Validation]', error);
      return { valid: false, error };
    }

    console.log('✅ [Validation] File validation passed');
    return { valid: true };
  }

  /**
   * Convert File to Blob
   * @param file - File object
   * @returns Blob object
   */
  fileToBlob(file: File): Blob {
    return new Blob([file], { type: file.type });
  }

  /**
   * Get public URL from blob name
   * @param blobName - Blob name
   * @returns Public image URL
   */
  getPublicUrl(blobName: string): string {
    if (!this.containerUrl) {
      throw new Error('Azure Storage not configured');
    }
    return `${this.containerUrl}/${blobName}`;
  }
}

// Export singleton instance
export const azureBlobService = new AzureBlobStorageService();

export type { UploadImageResponse };

